/**
 * The virtual account P&L by category on screen (TMMAPP-0111; the tmm-api spec review's amendment,
 * D1; MM-D-124): Categories → "P&L" reads `GET /v1/virtual-accounts/{id}/pnl?breakdown=envelope` —
 * months as columns, income then expense rows, Uncategorized, Net — each category row expanding into
 * its envelopes. Every figure is served; the SPA adds only the per-kind subtotals of what it shows.
 *
 * The month range (TMMAPP-0113 4e, Operator 2026-10-07): the download range's presets as chips —
 * Year to date (the default: the server's own window, no months sent), Last 12 months, This month,
 * Last month, Last year — and Custom, with From / To month fields. The choice lives in the URL
 * (`from` / `to`, `YYYY-MM`); each change is one new request, never a slice of the last one
 * (MM-D-53). A range the server refuses shows on the control.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Box, Skeleton, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError } from '@/api/errors';
import { pnlApi, type Pnl } from '@/api/pnl';
import { ErrorState } from '@/components/ErrorState';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { PnlTable } from './PnlTable';
import { RangeChips } from './RangeChips';
import {
  isBadRange,
  isMonth,
  presetMonths,
  presetOf,
  type PnlMonths,
  type PnlPreset,
} from './pnlRange';

type State =
  | { status: 'ready'; pnl: Pnl }
  | { status: 'refused' }
  | { status: 'error'; error: unknown }
  | null;

export function VirtualAccountPnlPanel({
  virtualAccountId,
  currency,
  months,
  onMonths,
}: {
  virtualAccountId: number;
  currency: string | null;
  /** The URL's range; both empty = Year to date (the server's default window). */
  months: PnlMonths;
  onMonths: (next: PnlMonths) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  const [state, setState] = useState<State>(null);
  const [tick, setTick] = useState(0);
  const preset = presetOf(months, today);
  // Custom stays open once chosen, even on a range a preset also names.
  const [custom, setCustom] = useState(preset === null);
  const [draft, setDraft] = useState<PnlMonths>(() =>
    months.from === '' && months.to === '' ? presetMonths('ytd', today) : months,
  );
  const bad = custom && isBadRange(draft.from, draft.to);
  const from = months.from === '' ? undefined : months.from;
  const to = months.to === '' ? undefined : months.to;

  // One request per range (StrictMode's double effect included): the key is the range itself, and
  // only the latest request's answer is shown.
  const loaded = useRef<string | null>(null);
  const seq = useRef(0);
  const key = `${virtualAccountId}|${from ?? ''}|${to ?? ''}|${tick}`;
  useEffect(() => {
    if (loaded.current === key) return;
    loaded.current = key;
    const mine = ++seq.current;
    queueMicrotask(() => {
      if (seq.current !== mine) return;
      setState(null);
      pnlApi
        .virtualAccount(virtualAccountId, { breakdown: 'envelope', from, to })
        .then((pnl) => {
          if (seq.current === mine) setState({ status: 'ready', pnl });
        })
        .catch((error: unknown) => {
          if (seq.current !== mine) return;
          setState(
            isApiError(error) && error.code === ErrorCodes.VALIDATION_FAILED
              ? { status: 'refused' }
              : { status: 'error', error },
          );
        });
    });
  }, [key, virtualAccountId, from, to]);

  function choose(next: PnlPreset) {
    setCustom(false);
    const range = presetMonths(next, today);
    setDraft(range);
    onMonths(next === 'ytd' ? { from: '', to: '' } : range);
  }

  function editDraft(next: PnlMonths) {
    setDraft(next);
    if (isMonth(next.from) && isMonth(next.to) && !isBadRange(next.from, next.to)) onMonths(next);
  }

  let body;
  if (state === null) {
    body = <Skeleton variant="rounded" height={200} aria-busy="true" />;
  } else if (state.status === 'error') {
    body = (
      <ErrorState
        variant="section"
        error={state.error}
        what={t('errors.what.pnl')}
        onRetry={() => setTick((n) => n + 1)}
      />
    );
  } else if (state.status === 'refused') {
    body = null;
  } else {
    body = <PnlTable pnl={state.pnl} currency={currency} expandable label={t('pnl.tableLabel')} />;
  }

  return (
    <Box sx={{ borderTop: `1px solid ${tokens.line}`, p: '12px 20px 16px' }}>
      <RangeControl
        preset={custom ? null : preset}
        custom={custom}
        draft={draft}
        bad={bad}
        refused={state?.status === 'refused'}
        window={
          state?.status === 'ready'
            ? t('pnl.window', {
                from: formatCivilDate(state.pnl.from, locale),
                to: formatCivilDate(state.pnl.to, locale),
              })
            : null
        }
        onPreset={choose}
        onCustom={() => setCustom(true)}
        onDraft={editDraft}
      />
      {body}
    </Box>
  );
}

function RangeControl({
  preset,
  custom,
  draft,
  bad,
  refused,
  window,
  onPreset,
  onCustom,
  onDraft,
}: {
  preset: PnlPreset | null;
  custom: boolean;
  draft: PnlMonths;
  bad: boolean;
  refused: boolean;
  window: string | null;
  onPreset: (preset: PnlPreset) => void;
  onCustom: () => void;
  onDraft: (next: PnlMonths) => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const errorId = useId();
  const message = bad ? t('pnl.badRange') : refused ? t('pnl.rangeRefused') : null;
  return (
    <Box sx={{ mb: 1.5 }} data-pnl-range="">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <RangeChips preset={preset} custom={custom} onPreset={onPreset} onCustom={onCustom} />
        {window !== null && (
          <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
            {window}
          </Typography>
        )}
      </Box>
      {custom && (
        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 200px))' },
            gap: 1.5,
          }}
        >
          <TextField
            type="month"
            size="small"
            label={t('pnl.rangeFrom')}
            value={draft.from}
            onChange={(event) => onDraft({ ...draft, from: event.target.value })}
            error={bad}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: message !== null ? { 'aria-describedby': errorId } : {},
            }}
          />
          <TextField
            type="month"
            size="small"
            label={t('pnl.rangeTo')}
            value={draft.to}
            onChange={(event) => onDraft({ ...draft, to: event.target.value })}
            error={bad}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: message !== null ? { 'aria-describedby': errorId } : {},
            }}
          />
        </Box>
      )}
      {message !== null && (
        <Typography id={errorId} role="alert" sx={{ mt: 1, fontSize: 12, color: tokens.errorText }}>
          {message}
        </Typography>
      )}
    </Box>
  );
}
